import React, {useEffect, useRef, useState} from "react";
import {Anchor, Button, Table} from "@mantine/core";
import {nextStatusMapping} from "../HamperCodes";
import {useNavigate} from "react-router-dom";
import {showNotification} from "@mantine/notifications";
import {IconAlertCircle, IconTriangle, IconX} from "@tabler/icons-react";

const EmployeeCustomerTD = (props) => {
    const navigate = useNavigate();
    const timerRef = useRef(null);
    const intervalRef = useRef(null);
    const [progress, setProgress] = useState(0);
    const holdDuration = 1500;
    const hamper = props.hamper;
    const hamperStatus = hamper.status;
    const formattedDate = new Date(hamper.statusUpdatedAt).toLocaleString("en-US", {
        dateStyle: "short",
        timeStyle: "short",
    });
    const [showButtons, setShowButtons] = useState(props.status === "Pickup Pending");
    const [holdMessage, setHoldMessage] = useState("");
    const [alertLevel, setAlertLevel] = useState("");
    const [timeRemaining, setTimeRemaining] = useState("");

    let dropOffPref = hamper.customerPreferences?.find( item => item.preferenceType === "HAMPER_DROP_OFF_LOCATION" )?.preferenceValue || "Meet at door" ;
    let pickUpPref = hamper.customerPreferences?.find( item => item.preferenceType === "HAMPER_PICKUP_LOCATION" )?.preferenceValue || "Meet at door" ;
    if( dropOffPref?.includes("Concierge") ) {
        dropOffPref = "Concierge"
    }
    if( pickUpPref?.includes("Concierge") ) {
        pickUpPref = "Concierge"
    }

    const startHold = ( hamperCode, functionCall ) => {
        let start = Date.now();

        if( functionCall === "skipped" ) {
            setHoldMessage("Skipping Hamper in ");
        }
        else if ( functionCall === "next" ) {
            setHoldMessage("Updating to Next Status in ");
        }

        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - start;
            setProgress( Math.min( elapsed / holdDuration, 1) * 100)

            const remainingTime = Math.max(0, holdDuration - elapsed);
            const remainingSec = Math.floor( remainingTime / 1000 );
            const remainingMs = Math.floor( ( remainingTime % 1000 ) / 10 );
            const s = String( remainingSec ).padStart( 1, "0" );
            const m = String( remainingMs ).padStart( 2, "0" );

            if( alertLevel === "" ){
                setAlertLevel("alert-level-green");
            }

            if( remainingTime > 1000 && remainingSec <= 2000 ) {
                setAlertLevel("alert-level-yellow");
            }
            else if( remainingTime <= 1000 ) {
                setAlertLevel("alert-level-red");
            }

            setTimeRemaining(  `${s}.${m}`);
        }, 1);

        timerRef.current = setTimeout( () => {
            clearInterval( intervalRef.current );
            setProgress( 100 );
            setHoldMessage("");
            setTimeRemaining("");
            setAlertLevel("alert-level-green");

            if( functionCall === "skipped" ) {
                props.handleStatusUpdate(hamperCode, "PICKUP_SKIPPED");
            }
            else if ( functionCall === "next" ) {
                props.handleStatusUpdate(hamper.hamperCode, nextStatusMapping[hamper.status])
            }

            setShowButtons( false );
            handleShowButtons( new Date() );

            setTimeout( () => {
                setProgress( 0 );
            }, 500 );
        }, holdDuration)
    }

    const cancelHold = () => {
        clearTimeout( timerRef.current );
        clearInterval( intervalRef.current );
        setProgress( 0 );
    }

    useEffect(() => {
        const start = new Date(hamper.statusUpdatedAt);

        handleShowButtons( start );
    }, [])

    const handleShowButtons = ( start ) => {
        const interval = setInterval(() => {
            const now = new Date();
            const diffMs = now - start;
            if( diffMs >= 10 * 60 * 1000) { // 10 minute in ms
                setShowButtons(true);
                clearInterval( interval );
            }
        }, 1000);
    }

    return (
        <Table.Td className={"customerListTableDefinition"} id={hamper.customerId} colspan={5}>
            { progress > 0 &&
                <div style={{
                    position: 'absolute',
                    left: -4,
                    bottom: -4,
                    width: `${progress}%`,
                    borderRadius: '12px',
                    border: '2px solid transparent',
                    boxSizing: 'border-box',
                    zIndex: 0,
                    pointerEvents: 'none',
                    background: `#28a745`,
                    padding: '2px'
                }}>
                </div>
            }
            <div className={"customerRowSection customerRowHeader"}>
                <div className={"customerName"}>
                    {hamper.name}
                </div>
                <div className={"customerAddress"}>
                    <Anchor
                        href={`https://www.google.com/maps/search/?api=1&query=${hamper.encodedAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {hamper.address}
                    </Anchor>
                </div>
            </div>
            <div className={progress > 0 ? "customerRowSection customerRowData holdSectionMessage" : "customerRowSection customerRowData"}>
                <div className={"customerCode"}>
                    <div className={"customerSectionTitle"}>
                        Code
                    </div>
                    <div className={"customerSectionValue"}>
                        <Anchor
                            onClick={() => navigate(`/employee/update-tracking?code=${hamper.hamperCode}&status=${hamper.status}`, {
                                state: {from: "customerHamperList"},
                            })}
                            target="_self"
                            className={"hamperCodeLink"}
                        >
                            {hamper.hamperCode}
                        </Anchor>
                    </div>
                </div>
                <div className={"customerStatus"}>
                    <div className={"customerSectionTitle"}>
                        Status
                    </div>
                    <div className={"customerSectionValue"}>
                        {props.status}
                    </div>
                </div>
                { ["AWAITING_PICKUP", "PICKUP_SKIPPED","DELIVERED_TO_CUSTOMER"].includes( hamperStatus ) &&
                    <div className={"customerStatus"}>
                        <div className={"customerSectionTitle"}>
                            Pickup
                        </div>
                        <div className={"customerSectionValue"}>
                            { pickUpPref }
                        </div>
                    </div>
                }
                { "PICKED_UP_FROM_LAUNDROMAT" === hamperStatus &&
                    <div className={"customerStatus"}>
                        <div className={"customerSectionTitle"}>
                            Drop Off
                        </div>
                        <div className={"customerSectionValue"}>
                            { dropOffPref }
                        </div>
                    </div>
                }
                <div className={"customerFormattedDate"}>
                    <div className={"customerSectionTitle"}>
                        Last Updated
                    </div>
                    <div className={"customerSectionValue"}>
                        {formattedDate}
                    </div>
                </div>
                { progress > 0 &&
                    <div className={"holdMessageSection"}>
                        {holdMessage}
                        <div className={alertLevel}>  {timeRemaining}</div>
                    </div>
                }
            </div>
            { showButtons &&
                <div className={"customerRowSection customerRowCTA"}>
                    <div className={"customerUpdateStatus"}>
                        <Button
                            className={props.pickupSkip ? "customerUpdateStatusButton" : "customerUpdateStatusButton updateButtonSingle"}
                            radius="md"
                            onMouseDown={() => startHold(hamper.hamperCode, "next")}
                            onMouseUp={cancelHold}
                            onMouseLeave={cancelHold}
                            onTouchStart={() => startHold(hamper.hamperCode, "next")}
                            onTouchEnd={cancelHold}
                            onClick={(e) => {
                                e.preventDefault()
                            }}
                        >
                            Next Status
                        </Button>
                    </div>
                    <div className={"customerPickupSkipped"}>
                        <Anchor
                            className={ props.pickupSkip ? "customerPickupSkippedButton" : "customerPickupSkippedButton skippedUnavailable"}
                            aria-disabled={ !props.pickupSkip }
                            disabled={ !props.pickupSkip }
                            radius="md"
                            onMouseDown={() => {
                                if( props.pickupSkip ) {
                                    startHold(hamper.hamperCode, "skipped")
                                }
                            }}
                            onMouseUp={() => {
                                if( props.pickupSkip ) {
                                    cancelHold()
                                }
                            }}
                            onMouseLeave={() => {
                                if( props.pickupSkip ) {
                                    cancelHold()
                                }
                            }}
                            onTouchStart={() => {
                                if( props.pickupSkip ) {
                                    startHold(hamper.hamperCode, "skipped")
                                }
                            }}
                            onTouchEnd={() => {
                                if( props.pickupSkip ) {
                                    cancelHold()
                                }
                            }}
                            style={{
                                WebkitUserSelect: 'none',
                                userSelect: 'none',
                                WebkitTapHighlightColor: 'transparent',
                                touchAction: 'none'
                            }}
                            onClick={(e) => {
                                e.preventDefault()

                                if( !props.pickupSkip ) {
                                    showNotification({
                                        title: "Warning",
                                        message: "Unable to Skip Hamper at this time",
                                        color: "yellow",
                                        icon: <IconAlertCircle />,
                                        autoClose: 10000,
                                    });
                                }
                            }}
                        >
                            Hold to Skip
                        </Anchor>
                    </div>
                </div>
            }
        </Table.Td>
    )
}

export default EmployeeCustomerTD;